import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Descriptions,
  Select,
  Tag,
  Typography,
  Space,
  Button,
  message,
  Input,
  Tabs,
  Spin,
} from "antd";
import { CalendarOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { api } from "../../../../services/api";
import CommentsCard from "./comments";
import AssigneeSelect from "./AssigneeSelect";
import { useNavigate, useParams } from "react-router-dom";
import HistoryCard from "./HistoryCard";
import { getMenuFeatures } from "../../../../utils/media";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

export default function IssueDetails() {
  const [issue, setIssue] = useState<any>(null);
  const [user, setUser] = useState<any>({});
  const [users, setUsers] = useState<any[]>([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [menuId, setMenuId] = useState<string | null>(null);

  const feature = new Set(getMenuFeatures("ticketing"));
  const canEditAll = feature.has("isAdmin");

  const [isTitleEditable, setIsTitleEditable] = useState<boolean>(false);
  const [isDescriptionEditable, setIsDescriptionEditable] =
    useState<boolean>(false);
  const [updatedTitle, setUpdatedTitle] = useState<string>(issue?.title);
  const [updatedDescription, setUpdatedDescription] = useState<string>(
    issue?.description
  );

  const titleRef = useRef<any>(null);
  const descriptionRef = useRef<any>(null);
  const [menu, setMenu] = useState([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const menuList = JSON.parse(window.localStorage.getItem("menu-list"));
    menuList && setMenu(menuList?.data);
    menuList && setMenuId(menuList?._id);
  }, []);

  const fetchDetails = async () => {
    if (!id) {
      console.error("Issue ID is missing");
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.getTicketDetail(id, menuId, menu);
      console.log("🚀 ~ fetchDetails ~ response:", response);
      setIssue(response.ticket);
      if (response.user) {
        setUser(response.user);
        setUsers(Object.values(response.user));
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log("404 error: Ticket not found.");
        setIsError(true);
      } else {
        console.log("Error fetching details:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (menuId) {
      fetchDetails();
    }
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuId]);

  const updateTicket = async (id: string, body: any) => {
    try {
      await api.updateTicket(id, body);
      message.success("Ticket updated successfully!");
      // fetchDetails();
    } catch (error) {
      message.error("Failed to update the ticket.");
      console.error("Error updating ticket:", error);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUpdatedTitle(e.target.value);
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setUpdatedDescription(e.target.value);
  };

  const handleUpdateTitle = () => {
    updateTicket(id, { title: updatedTitle });
    setIsTitleEditable(false);
  };

  const handleUpdateDescription = () => {
    updateTicket(id, { description: updatedDescription });
    setIsDescriptionEditable(false);
  };
  const handleCancelDescriptionEdit = () => {
    setUpdatedDescription(issue?.description);
    setIsDescriptionEditable(false);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      titleRef.current &&
      !titleRef.current.contains(e.target) &&
      descriptionRef.current &&
      !descriptionRef.current.contains(e.target)
    ) {
      setIsTitleEditable(false);
      setIsDescriptionEditable(false);
    }
  };

  const handleCancelTitleEdit = () => {
    setUpdatedTitle(issue?.title);
    setIsTitleEditable(false);
  };

  if (isError) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <Text type="danger">
          Ticket not found. Please check the ID and try again.
        </Text>
      </div>
    );
  }

  if (isLoading || !issue) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{
          marginRight: 16,
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
        }}
      >
        Back
      </Button>
      <Text type="secondary">Ticket #{issue?.id}</Text>

      <div style={{ paddingTop: 16, paddingBottom: 32 }}>
        <Space align="center" style={{ marginBottom: 16, paddingLeft: 16 }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {/* Title: Show text or editable input */}
              {canEditAll && isTitleEditable ? (
                <Input
                  ref={titleRef}
                  defaultValue={issue?.title}
                  value={updatedTitle}
                  onChange={handleTitleChange}
                  style={{ fontWeight: "bold", fontSize: "20px" }}
                  autoFocus
                />
              ) : (
                <span onClick={() => setIsTitleEditable(true)}>
                  {issue?.title}
                </span>
              )}
            </Title>
            {/* Show the update button next to title */}
            {canEditAll && isTitleEditable && (
              <div>
                <Button
                  type="primary"
                  style={{ marginTop: 16, marginRight: 8 }}
                  onClick={handleUpdateTitle}
                >
                  Update Title
                </Button>
                <Button
                  type="default"
                  style={{ marginTop: 16 }}
                  onClick={handleCancelTitleEdit}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </Space>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <Card title="Details">
              <Text strong>Description</Text>
              {/* Description: Show text or editable textarea */}
              {canEditAll && isDescriptionEditable ? (
                <TextArea
                  ref={descriptionRef}
                  defaultValue={issue?.description}
                  value={updatedDescription}
                  onChange={handleDescriptionChange}
                  rows={4}
                  autoFocus
                />
              ) : (
                <p
                  style={{ marginTop: 8 }}
                  onClick={() => setIsDescriptionEditable(true)}
                >
                  {issue?.description}
                </p>
              )}

              {/* Buttons to update Title and Description */}
              {canEditAll && isDescriptionEditable && (
                <div>
                  <Button
                    type="primary"
                    style={{ marginTop: 16, marginRight: 8 }}
                    onClick={handleUpdateDescription}
                  >
                    Update Description
                  </Button>
                  <Button
                    type="default"
                    style={{ marginTop: 16 }}
                    onClick={handleCancelDescriptionEdit}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </Card>

            <Card
              title={
                <Space style={{ width: "100%" }}>
                  <span>Activity</span>
                </Space>
              }
              style={{ marginTop: 16 }}
            >
              <Tabs defaultActiveKey="comments">
                <TabPane tab="Comments" key="comments">
                  <CommentsCard
                    commentsData={issue?.comments}
                    issue={issue}
                    user={user}
                  />
                </TabPane>
                <TabPane tab="History" key="history">
                  <HistoryCard issue={issue} user={user} />
                </TabPane>
              </Tabs>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card title="Status & Priority">
              <Descriptions column={1} colon={false}>
                <Descriptions.Item label="Status">
                  <Select
                    options={[
                      { value: "open", label: "New" },
                      { value: "progress", label: "In Progress" },
                      { value: "hold", label: "Hold" },
                      { value: "resolved", label: "Resolved" },
                      { value: "rejected", label: "Rejected" },
                      { value: "closed", label: "Closed" },
                    ]}
                    value={issue.status}
                    style={{ width: "100%" }}
                    onChange={(value) => {
                      updateTicket(id, { status: value });
                      setIssue({ ...issue, status: value });
                    }}
                  ></Select>
                </Descriptions.Item>

                <Descriptions.Item label="Priority">
                  <Select
                    value={issue?.priority}
                    disabled={!canEditAll}
                    style={{ width: "100%" }}
                    onChange={(value) => {
                      updateTicket(id, { priority: value });
                      setIssue({ ...issue, priority: value });
                    }}
                  >
                    <Select.Option value={1}>P1</Select.Option>
                    <Select.Option value={2}>P2</Select.Option>
                    <Select.Option value={3}>P3</Select.Option>
                    <Select.Option value={4}>P4</Select.Option>
                  </Select>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Assignment & Category" style={{ marginTop: 16 }}>
              <Descriptions column={1} colon={false}>
                <Descriptions.Item label="Assignee">
                  <AssigneeSelect
                    data={users}
                    value={issue?.assignee_id}
                    onChange={(value) => {
                      setIssue({
                        ...issue,
                        assignee_id: value?.value || value,
                      });
                      updateTicket(id, { assignee_id: value?.value || value });
                    }}
                    onSearchUser={async (searchText: string) => {
                      try {
                        const response = await api.getAllUsers({
                          search: searchText,
                        });
                        setUsers(response?.data || []);
                      } catch (error) {
                        console.error("Error searching users:", error);
                      }
                    }}
                  />
                </Descriptions.Item>

                <Descriptions.Item label="Reporter">
                  <Tag color="blue">{user[issue?.user_id]?.name}</Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Type">
                  <Tag color={issue.type === "bug" ? "red" : "blue"}>
                    {issue.type}
                  </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Category">
                  <Tag>{issue.reason_code}</Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Dates & Time" style={{ marginTop: 16 }}>
              <Descriptions column={1} colon={false}>
                <Descriptions.Item label="Created">
                  <CalendarOutlined />
                  {new Date(issue.created_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}{" "}
                  -{" "}
                  {new Date(issue.created_at).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: true,
                  })}
                </Descriptions.Item>
                <Descriptions.Item label="Updated">
                  <CalendarOutlined />
                  {new Date(issue.updated_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}{" "}
                  -{" "}
                  {new Date(issue.updated_at).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: true,
                  })}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Tags */}
            <Card title="Tags" style={{ marginTop: 16 }}>
              <Select
                defaultValue={issue.tags.map((e) => ({ value: e, label: e }))}
                open={false}
                disabled={!canEditAll}
                mode="tags"
                style={{ width: "100%" }}
                placeholder="Add tags"
                tokenSeparators={[","]}
                onChange={(tags) => {
                  updateTicket(id, { tags });
                }}
              ></Select>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}
